import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, User, ShieldOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useCompanyLogo } from "@/hooks/use-company-logo";

interface SellerLoginProps {
  onLoginSuccess: () => void;
}

export default function SellerLogin({ onLoginSuccess }: SellerLoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const logoUrl = useCompanyLogo();

  const { data: portalStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/seller-auth/portal-status"],
    staleTime: 30_000,
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await apiRequest("POST", "/api/seller-auth/login", { username, password });
      onLoginSuccess();
    } catch (err: any) {
      const message = err?.message || "Fel användarnamn eller lösenord";
      toast({ title: "Inloggning misslyckades", description: message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const portalClosed = portalStatus?.enabled === false;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={logoUrl} alt="Rätt Order" className="h-16 w-auto max-w-[200px] object-contain" data-testid="img-seller-login-logo" />
          </div>
          <CardTitle className="text-2xl">Säljportal</CardTitle>
          <CardDescription>Logga in för att hantera kunder och ordrar</CardDescription>
        </CardHeader>
        <CardContent>
          {portalClosed ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="seller-portal-closed">
              <ShieldOff className="h-12 w-12 text-muted-foreground" />
              <p className="font-medium">Säljportalen är stängd</p>
              <p className="text-sm text-muted-foreground">Kontakta administratören för mer information.</p>
            </div>
          ) : (
            <>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Användarnamn</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Ditt användarnamn"
                      className="pl-9"
                      required
                      data-testid="input-seller-username"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Lösenord</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Ditt lösenord"
                      className="pl-9"
                      required
                      data-testid="input-seller-password"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading} data-testid="button-seller-login">
                  {isLoading ? "Loggar in..." : "Logga in"}
                </Button>
              </form>
              <div className="mt-6 pt-4 border-t text-center text-sm text-muted-foreground">
                <p>Kontakta din administratör om du inte har inloggningsuppgifter.</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
